/**
 * KAN-746: заказы клиента в карточке клиента интернет-магазина: последние
 * заказы по всем статусам (один запрос tab=all), ссылка "Все заказы клиента" и кнопка "Новый
 * заказ" (новый черновик или текущая корзина клиента, затем переход в заказ).
 */
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Plus, ShoppingCart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { OrdersTable } from '@/components/orders/OrdersTable';
import { useClientRecentOrders, useCreateOrder } from '@/hooks/useOrders';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { MODE_LABELS } from '@/lib/modeLabels';

const LABELS = MODE_LABELS.ecommerce;

export function ClientOrdersCard({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const recent = useClientRecentOrders(clientId);
  const orders = recent.data?.results ?? [];
  const create = useCreateOrder();

  return (
    <Card data-testid="client-orders-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          {LABELS.clientDealsTitle}
          <Badge variant="outline" className="ml-2">
            {recent.data?.count ?? 0}
          </Badge>
        </CardTitle>
        <Button
          type="button"
          size="sm"
          data-testid="client-new-order"
          disabled={create.isPending}
          onClick={() =>
            create.mutate(clientId, { onSuccess: (order) => navigate(`/orders/${order.id}`) })
          }
        >
          {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
          Новый заказ
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {create.error && (
          <p role="alert" className="text-sm text-red-600" data-testid="client-new-order-error">
            {extractErrorMessage(create.error, 'Не удалось создать заказ')}
          </p>
        )}
        {recent.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : recent.isError ? (
          <p className="py-4 text-sm text-red-600">Не удалось загрузить заказы</p>
        ) : orders.length > 0 ? (
          <div className="-mx-6">
            <OrdersTable orders={orders} showClient={false} showStatus />
          </div>
        ) : (
          <div data-testid="client-orders-empty" className="py-8 text-center text-gray-500">
            <ShoppingCart className="mx-auto mb-2 h-8 w-8 text-gray-300" />
            <p>{LABELS.clientDealsEmpty}</p>
          </div>
        )}
        <Link
          to={`/orders?client=${encodeURIComponent(clientId)}`}
          className="inline-block text-sm text-blue-600 hover:underline"
          data-testid="client-all-orders"
        >
          Все заказы клиента
        </Link>
      </CardContent>
    </Card>
  );
}
